import { useTonConnectUI } from '@tonconnect/ui-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { connectWalletApi, fetchUserWalletNonce } from '@api'
import { useAppActions } from '@store'

const utf8len = (s: string) => new TextEncoder().encode(s).length

interface UseConnectWalletReturn {
  connect: () => Promise<void>
  busy: boolean
  error: string | null
}

export const useConnectWallet = (): UseConnectWalletReturn => {
  const [tonConnectUI] = useTonConnectUI()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nonceRef = useRef<string | null>(null)
  const { followRefresh } = useAppActions()

  const connect = useCallback(async () => {
    followRefresh()
    setBusy(true)
    setError(null)
    tonConnectUI.setConnectRequestParameters({ state: 'loading' })

    try {
      const { data: nonce } = await fetchUserWalletNonce()

      if (!nonce) {
        throw new Error('Failed to get nonce')
      }

      nonceRef.current = nonce
      tonConnectUI.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: nonce },
      })
      tonConnectUI.openModal()
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to get nonce'
      setError(errorMessage)
      tonConnectUI.setConnectRequestParameters(null)
      setBusy(false)
    }
  }, [tonConnectUI])

  useEffect(() => {
    const unsub = tonConnectUI.onStatusChange(async (wallet) => {
      if (!wallet) return

      const proofItem = wallet.connectItems?.tonProof
      if (!proofItem || !('proof' in proofItem) || !nonceRef.current) return

      const proof = proofItem.proof

      try {
        const domainValue =
          typeof proof.domain === 'string' ? proof.domain : proof.domain?.value
        const domainLen =
          typeof proof.domain === 'string'
            ? utf8len(proof.domain)
            : proof.domain?.lengthBytes ?? utf8len(domainValue || '')

        const { ok } = await connectWalletApi({
          proof: {
            domain: { value: domainValue, lengthBytes: domainLen },
            payload: proof.payload,
            signature: proof.signature,
            timestamp: proof.timestamp,
          },
          wallet: {
            address: wallet.account.address,
            publicKey: wallet.account.publicKey || '',
          },
        })

        if (!ok) {
          throw new Error('Failed to connect wallet')
        }
      } catch (e) {
        const errorMessage =
          e instanceof Error ? e.message : 'Failed to connect wallet'
        setError(errorMessage)
      } finally {
        setBusy(false)
        tonConnectUI.setConnectRequestParameters(null)
        nonceRef.current = null
      }
    })

    return () => unsub()
  }, [tonConnectUI])

  return {
    connect,
    busy,
    error,
  }
}
